import React, { useState, useEffect } from 'react';
import { activateErpIntegration, generateSsoLink } from 'Shared/util/web-api';
import { useLocalNotificationBanner, useTranslation } from 'Shared/util/custom-hooks';
import { ErpIntegrationProviderIdResource } from 'Shared/util/types';
import useCustomerContext from 'Contexts/customer-context';
import { scrollToTop } from 'Shared/util/scroll';
import Page from 'Shared/components/page/page';
import Button from 'Shared/components/button';
import Notice from 'Shared/components/notice';
import IntroductionSection from './sections/introduction-section';
import UserSection from './sections/user-section';
import styles from './acconomy-setup-page.css';


const renderOnGoingNotice = (ssoLink: string | undefined, t: any) => (
  <div className={styles.noticeContainer}>
    <Notice
      svgPath="/coffee-cup.svg"
      content={t.youCanStartSellingYourInvoicesAsSoon}
      button={{
        text: t.go_to_acconomy,
        onClick: () => window.open(ssoLink),
        disabled: !ssoLink,
      }}
    />
  </div>
);

const AcconomySetupPage = () => {
  const [allowActivation, setAllowActivation] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [showSsoLink, setShowSsoLink] = useState(false);
  const [ssoLink, setSsoLink] = useState<string>();
  const t = useTranslation(x => x.acconomy);
  const tg = useTranslation(x => x.global);
  const { customerData, loadingCustomerData, updateActiveErpIntegration } = useCustomerContext();
  const {
    NotificationBannerType,
    addLocalNotificationBanner,
    removeAllLocalNotificationBanners,
  } = useLocalNotificationBanner();

  const {
    complianceApprovedDate,
    activeErpIntegration
  } = customerData || {};

  useEffect(() => {
    if (loadingCustomerData) {
      return;
    } else if (!activeErpIntegration) {
      setAllowActivation(!!complianceApprovedDate);
    } else {
      switch (activeErpIntegration.providerId) {
        case ErpIntegrationProviderIdResource.Acconomy:
          setAllowActivation(false);
          break;

        case ErpIntegrationProviderIdResource.Fortnox:
          addLocalNotificationBanner(t.format(t.inOrderToLinkAcconomyWithMyPages, { activeProviderName: activeErpIntegration.providerName }), NotificationBannerType.Attention, false);
          break;

        case ErpIntegrationProviderIdResource.Undefined:
        default:
          addLocalNotificationBanner(tg.thereWasTechnicalError, NotificationBannerType.Error, false);
          break;
      }
    }
  }, [loadingCustomerData, activeErpIntegration, complianceApprovedDate]);

  useEffect(() => {
    if (!loadingCustomerData && !complianceApprovedDate) {
      addLocalNotificationBanner(x => x.acconomy_disabled_pending_approved_agreement);
    }
  }, [loadingCustomerData, complianceApprovedDate]);

  useEffect(() => {
    if (showSsoLink) {
      scrollToTop();
    }
  }, [showSsoLink]);

  const handleActivateErpIntegration = () => {
    removeAllLocalNotificationBanners();
    setIsActivating(true);

    /* NOTE: React might show an incorrect error ("Warning: Can't perform a React state update on an unmounted component...") if component
    have been unmounted on "setState". This should be fixed in upcoming release https://github.com/facebook/react/pull/22114 */
    activateErpIntegration({ providerId: ErpIntegrationProviderIdResource.Acconomy })
      .then((activationResult) => {
        generateSsoLink({ path: 'invoices', providerId: ErpIntegrationProviderIdResource.Acconomy })
          .then((ssoLinkResult) => setSsoLink(ssoLinkResult))
          .finally(() => {
            updateActiveErpIntegration(activationResult);
            setShowSsoLink(true);
            setIsActivating(false);
          });
      })
      .catch(() => {
        setIsActivating(false);
        addLocalNotificationBanner(t.errorOccurredWhileConnectingToAcconomy, NotificationBannerType.Error);
      });
  };

  return (
    <Page
      title={{
        text: t.getStartedWithAcconomy,
        reducedMarginBottom: true,
      }}
      narrowWidth
      loading={loadingCustomerData}
    >
      {showSsoLink && renderOnGoingNotice(ssoLink, t)}
      <IntroductionSection />
      <UserSection />
      {!showSsoLink && (
        <Button
          buttonType="action-primary"
          text={t.linkAccount}
          onClick={handleActivateErpIntegration}
          isLoading={isActivating}
          attributes={{ disabled: !allowActivation }}
          className={styles.activateButton}
        />
      )}
    </Page>
  );
};

export default AcconomySetupPage;
